import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Plus, Mail, UserPlus, Trash2, Phone, MoreVertical, 
  ChevronUp, ChevronDown, Search, Filter, SortAsc, SortDesc, X,
  Contact, RefreshCw, CheckCircle2, Send, Copy, Shield
} from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import { getToken } from '../utils/auth';
import LoadingScreen from '../components/LoadingScreen';

const API_BASE = import.meta.env.VITE_API_BASE;

// User state mapping
const UserStates = {
  'PendingRegistration': { label: 'Pending', color: 'text-[#725600] bg-[#FFF4C2]' },
  'PendingMailVerification': { label: 'Pending', color: 'text-[#725600] bg-[#FFF4C2]' },
  'PendingPhoneVerification': { label: 'Pending', color: 'text-[#725600] bg-[#FFF4C2]' },
  'Active': { label: 'Active', color: 'text-green-500 bg-green-100' },
  'Blocked': { label: 'Blocked', color: 'text-red-500 bg-red-100' }
};

// Permissions mapping
const Permissions = {
  'Anonymous': { label: 'Anonymous', color: 'bg-gray-500 text-white' },
  'User': { label: 'User', color: 'bg-blue-500 text-white' },
  'Admin': { label: 'Admin', color: 'bg-purple-500 text-white' },
  'Owner': { label: 'Owner', color: 'bg-indigo-500 text-white' }
};

// Role priority mapping for sorting
const RolePriority = {
  'Owner': 1,
  'Admin': 2,
  'User': 3,
  'Anonymous': 4
};

function UserAvatar({ user }) {
  const [imageError, setImageError] = useState(false);

  // Get initials from full name or email
  const getInitials = () => {
    if (user.fullName) {
      const nameParts = user.fullName.split(' ').filter(part => part.length > 0);
      if (nameParts.length >= 2) {
        return (nameParts[0][0] + nameParts[1][0]).toUpperCase();
      }
      return nameParts[0][0].toUpperCase();
    }
    return user.email[0].toUpperCase();
  };

  if (user.avatarUrl && !imageError) {
    return (
      <div className="w-10 h-10 rounded-full overflow-hidden ring-1 ring-purple-100/20">
        <img
          src={user.avatarUrl}
          alt={user.fullName || user.email}
          className="w-full h-full object-cover"
          onError={() => setImageError(true)}
        />
      </div>
    );
  }

  return (
    <div
      className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium text-white ring-1 ring-purple-100/20"
      style={{ backgroundColor: '#B356C6' }}
    >
      {getInitials()}
    </div>
  );
}

export default function UsersManagement() {
  const { userData } = useUser();
  const navigate = useNavigate();

  // Add animation keyframes style
  const animationStyle = `
    @keyframes bounce {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.1); }
    }

    select {
      background-color: #eff6ff !important;
      color: #1e40af !important;
    }

    select option {
      background-color: #eff6ff !important;
      color: #1e40af !important;
    }

    select option:checked {
      background-color: #dbeafe !important;
      color: #1e40af !important;
    }

    select option:hover {
      background-color: #bfdbfe !important;
    }

    select:focus {
      background-color: #eff6ff !important;
      color: #1e40af !important;
    }

    select:focus option {
      background-color: #eff6ff !important;
      color: #1e40af !important;
    }
    
    /* Custom scrollbar styling */
    ::-webkit-scrollbar {
      width: 8px;
    }
    
    ::-webkit-scrollbar-track {
      background: transparent;
      margin: 4px;
    }
    
    ::-webkit-scrollbar-thumb {
      background: rgba(156, 163, 175, 0.5);
      border-radius: 20px;
      transition: all 0.2s ease;
    }
    
    ::-webkit-scrollbar-thumb:hover {
      background: rgba(156, 163, 175, 0.8);
    }
    
    /* Tooltip styles */
    .group:hover .group-hover\\:opacity-100 {
      opacity: 1;
      z-index: 99999 !important;
    }
    
    /* Tooltip elements */
    .absolute.bottom-full {
      z-index: 99999 !important;
    }
    
    /* Ensure rows have appropriate stacking */
    .hover\\:bg-\\[var\\(--hover-light\\)\\] {
      position: relative;
    }
    
    .hover\\:bg-\\[var\\(--hover-light\\)\\]:hover {
      z-index: 10;
    }
  `;

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [users, setUsers] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showSortModal, setShowSortModal] = useState(false);
  const [showConfirmCancelModal, setShowConfirmCancelModal] = useState(false);
  const [showChangeRoleModal, setShowChangeRoleModal] = useState(false);
  const [showRemoveUserModal, setShowRemoveUserModal] = useState(false);
  const [showChangeTeamModal, setShowChangeTeamModal] = useState(false);
  const [selectedInviteToCancel, setSelectedInviteToCancel] = useState(null);
  const [tempSortConfig, setTempSortConfig] = useState({ key: 'status', direction: 'asc' });
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState('User');
  const [selectedUserTeam, setSelectedUserTeam] = useState(null);
  const [showCreateUserTeamInput, setShowCreateUserTeamInput] = useState(false);
  const [newUserTeamName, setNewUserTeamName] = useState('');
  const [userTeamError, setUserTeamError] = useState('');
  const [changingTeam, setChangingTeam] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState('User');
  const [inviteTeam, setInviteTeam] = useState(null);
  const [selectedTeamOption, setSelectedTeamOption] = useState('noTeam');
  const [teams, setTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [showCreateTeamInput, setShowCreateTeamInput] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [creatingTeam, setCreatingTeam] = useState(false);
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [teamError, setTeamError] = useState('');
  const [success, setSuccess] = useState('');
  const [inviting, setInviting] = useState(false);
  const [changingRole, setChangingRole] = useState(false);
  const [removingUser, setRemovingUser] = useState(false);
  const mountedRef = useRef(false);
  const [sortConfig, setSortConfig] = useState({ key: 'status', direction: 'asc' });
  const [columnMenuOpen, setColumnMenuOpen] = useState(null);
  const [actionMenuOpen, setActionMenuOpen] = useState(null);
  const menuRef = useRef(null);
  const actionMenuRef = useRef(null);
  const [filters, setFilters] = useState({});
  const [searchValues, setSearchValues] = useState({});
  const [dropdownPosition, setDropdownPosition] = useState('bottom');
  const [openMenuId, setOpenMenuId] = useState(null);
  const [buttonPosition, setButtonPosition] = useState({ bottom: 0, left: 0 });
  const [isRemoveConfirmed, setIsRemoveConfirmed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [teamFilter, setTeamFilter] = useState('all'); // 'all' or team ID
  const [showTeamFilter, setShowTeamFilter] = useState(false);
  const [showDesktopTeamFilter, setShowDesktopTeamFilter] = useState(false);
  const [showMobileTeamFilter, setShowMobileTeamFilter] = useState(false);
  const teamFilterRef = useRef(null);
  const desktopTeamFilterRef = useRef(null);
  const mobileTeamFilterRef = useRef(null);

  const sortData = (data) => {
    const sortedData = [...data].sort((a, b) => {
      let comparison = 0;
      
      switch (sortConfig.key) {
        case 'role':
          comparison = RolePriority[a.permissionsId] - RolePriority[b.permissionsId];
          if (comparison === 0) {
            return (a.fullName || a.email).localeCompare(b.fullName || b.email);
          }
          break;
        case 'name':
          comparison = (a.fullName || a.email).localeCompare(b.fullName || b.email);
          break;
        case 'email':
          comparison = a.email.localeCompare(b.email);
          break;
        case 'phone':
          const phoneA = a.phoneCountry && a.phone ? `+${a.phoneCountry}${a.phone}` : '';
          const phoneB = b.phoneCountry && b.phone ? `+${b.phoneCountry}${b.phone}` : '';
          comparison = phoneA.localeCompare(phoneB);
          break;
        case 'status':
          // First sort by status
          comparison = a.userStateId.localeCompare(b.userStateId);
          // If status is the same, sort by role (Owner > Admin > User)
          if (comparison === 0) {
            comparison = RolePriority[a.permissionsId] - RolePriority[b.permissionsId];
            // If role is also the same, sort by name
            if (comparison === 0) {
              comparison = (a.fullName || a.email).localeCompare(b.fullName || b.email);
            }
          }
          break;
        case 'teamName':
          const teamA = a.teamName || '';
          const teamB = b.teamName || '';
          comparison = teamA.localeCompare(teamB);
          break;
        default:
          comparison = 0;
      }
      
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });

    return sortedData;
  };

  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ChevronUp className="w-4 h-4 opacity-0 group-hover:opacity-100 text-[var(--text-primary)]" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ChevronUp className="w-4 h-4 text-[var(--text-primary)]" />
      : <ChevronDown className="w-4 h-4 text-[var(--text-primary)]" />;
  };

  const fetchUsers = async () => {
    try {
      const token = getToken();
      if (!token) {
        navigate('/login');
        return;
      }

      setError(''); // Clear any existing errors
      setSuccess(''); // Clear any existing success messages
      setRefreshing(true); // Set refreshing state
      // Don't clear existing users array during refresh to avoid flashing "No users found"
      // setUsers([]); // Clear the users list before fetching

      // Start fetching teams in the background at the same time
      fetchTeams();

      const response = await fetch(`${API_BASE}/company/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data || []);
      } else if (response.status === 401) {
        navigate('/login');
        return;
      } else {
        throw new Error('Failed to fetch users');
      }
    } catch (err) {
      setError('Failed to fetch users. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false); // Clear refreshing state
    }
  };

  const fetchTeams = async () => {
    try {
      const token = getToken();
      if (!token) {
        navigate('/login');
        return;
      }

      setLoadingTeams(true);

      const response = await fetch(`${API_BASE}/company/teams`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTeams(data || []);
      } else if (response.status === 401) {
        navigate('/login');
        return;
      }
    } catch (err) {
      // Error handling
    } finally {
      setLoadingTeams(false);
    }
  };

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      if (userData) {
        if (!['Owner', 'Admin'].includes(userData.permissionsId)) {
          navigate('/home');
          return;
        }
        fetchUsers();
        // Teams data is now automatically fetched by fetchUsers
      } else {
        navigate('/login');
      }
    }
  }, [userData, navigate]);

  // Add a utility function for consistent team name formatting to match C# backend
  const formatTeamName = (name) => {
    if (!name || name.trim() === '') 
      return '';
    
    // Trim and replace multiple spaces with a single space
    const trimmedName = name.trim().replace(/\s+/g, ' ');
    
    // Capitalize first character if it's an English letter (ASCII <= 127)
    let formatted = trimmedName;
    if (trimmedName.length > 0) {
      const firstChar = trimmedName.charAt(0);
      if (/^[a-zA-Z]$/.test(firstChar) && firstChar.charCodeAt(0) <= 127) {
        formatted = firstChar.toUpperCase() + trimmedName.slice(1);
      }
    }
    
    return formatted;
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    
    if (inviting) return;

    // Validate email format
    if (!inviteEmail) {
      setFormError('Email address is required');
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail)) {
      setFormError('Please enter a valid email address');
      return;
    }
    
    // Validate team name if create team option is selected
    if (selectedTeamOption === 'assignTeam' && showCreateTeamInput && newTeamName.trim() === '') {
      setTeamError('Team name is required when creating a new team');
      return;
    }
    
    // Check if user entered a team name but didn't press Add
    if (selectedTeamOption === 'assignTeam' && showCreateTeamInput && newTeamName.trim() !== '') {
      setTeamError('Please click "Add" to add this team before sending the invitation');
      return;
    }

    setInviting(true);
    setError('');
    setFormError('');
    setTeamError('');

    try {
      const token = getToken();
      if (!token) {
        navigate('/login');
        return;
      }

      let teamIdToUse = inviteTeam;
      let newTeamCreated = false;
      let newTeam = null; // Define the newTeam variable at the top level
      
      // We'll let the backend handle team creation based on the team name

      // Prepare invitation data
      const inviteData = {
        email: inviteEmail,
        fullName: inviteName.trim() || undefined,
        permissionsId: inviteRole
      };

      // Add team name if selected
      if (selectedTeamOption === 'assignTeam') {
        if (showCreateTeamInput && newTeamName.trim()) {
          // If user is creating a new team, send the formatted name
          inviteData.teamName = formatTeamName(newTeamName.trim());
        } else if (inviteTeam) {
          // If user selected an existing team
          const selectedTeam = teams.find(t => t.id.toString() === inviteTeam.toString());
          if (selectedTeam) {
            inviteData.teamName = formatTeamName(selectedTeam.name);
          }
        }
      }

      const response = await fetch(`${API_BASE}/account/invite`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(inviteData)
      });

      if (response.ok) {
        // Show success message in the main view
        setSuccess(inviteData.teamName ? 'Invitation sent successfully with team assignment' : 'Invitation sent successfully');
        // Close modal immediately
        setShowInviteModal(false);
        // Reset form
        setInviteEmail('');
        setInviteName('');
        setInviteRole('User');
        setInviteTeam(null);
        setSelectedTeamOption('noTeam');
        setShowCreateTeamInput(false);
        setNewTeamName('');
        setTeamError('');
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSuccess('');
        }, 3000);
        // Refresh users list
        fetchUsers();
      } else {
        // Check for specific error status codes
        if (response.status === 418) {
          throw new Error('This email is already a member of a different company. use different mail or contact support.');
        } else {
          const data = await response.json();
          throw new Error(data.message || 'Failed to send invitation');
        }
      }
    } catch (err) {
      setFormError(err.message || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleCancelInvite = async (email) => {
    try {
      const token = getToken();
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${API_BASE}/account/invite`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email
        })
      });

      if (response.ok) {
        setSuccess('Invitation cancelled successfully');
        // Clear success after 3 seconds
        setTimeout(() => {
          setSuccess('');
        }, 3000);
        // Refresh users list
        fetchUsers();
      } else {
        const data = await response.json();
        throw new Error(data.message || 'Failed to cancel invitation');
      }
    } catch (err) {
      setError(err.message || 'Failed to cancel invitation');
    } finally {
      setSelectedInviteToCancel(null);
      setShowConfirmCancelModal(false);
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setColumnMenuOpen(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update the useEffect for action menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (actionMenuOpen !== null && 
          !event.target.closest('[data-menu-button]') && 
          !event.target.closest('[data-menu-content]')) {
        setActionMenuOpen(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [actionMenuOpen]);

  // Click outside handler for team filter dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (desktopTeamFilterRef.current && !desktopTeamFilterRef.current.contains(event.target)) {
        setShowDesktopTeamFilter(false);
      }
      if (mobileTeamFilterRef.current && !mobileTeamFilterRef.current.contains(event.target)) {
        setShowMobileTeamFilter(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const filterData = (data) => {
    // Apply main search query filter
    let filteredData = data;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filteredData = data.filter(item => {
        const nameMatch = (item.fullName || '').toLowerCase().includes(query);
        const emailMatch = (item.email || '').toLowerCase().includes(query);
        return nameMatch || emailMatch;
      });
    }
    
    // Apply team filter
    if (teamFilter !== 'all') {
      if (teamFilter === 'noTeam') {
        filteredData = filteredData.filter(item => !item.teamId && !item.teamName);
      } else {
        // Find the target team name for comparison
        const targetTeam = teams.find(team => team.id.toString() === teamFilter.toString());
        const targetTeamName = targetTeam?.name;
        
        // Filter by either teamId or teamName match
        filteredData = filteredData.filter(item => {
          const teamIdMatch = item.teamId?.toString() === teamFilter.toString();
          const teamNameMatch = targetTeamName && item.teamName === targetTeamName;
          return teamIdMatch || teamNameMatch;
        });
      }
    }
    
    // Apply existing filters
    return filteredData.filter(item => {
      for (const [key, value] of Object.entries(filters)) {
        if (!value || value.length === 0) continue;
        
        switch (key) {
          case 'role':
            const rolePattern = value.map(v => v.toLowerCase());
            if (!rolePattern.includes(item.permissionsId.toLowerCase())) return false;
            break;
          case 'status':
            const statusPattern = value.map(v => v.toLowerCase());
            // Special handling for Pending status
            if (statusPattern.includes('pending')) {
              if (!item.userStateId.toLowerCase().startsWith('pending') && 
                  !statusPattern.includes(item.userStateId.toLowerCase())) {
                return false;
              }
            } else if (!statusPattern.includes(item.userStateId.toLowerCase())) {
              return false;
            }
            break;
          default:
            break;
        }
      }

      for (const [key, value] of Object.entries(searchValues)) {
        if (!value) continue;
        
        // Convert search pattern to regex pattern
        // Support both wildcards (* and ?) and contains matching
        const searchPattern = value.toLowerCase()
          .replace(/[.+^${}()|[\]\\]/g, '\\$&') // escape regex special chars except * and ?
          .replace(/\*/g, '.*')
          .replace(/\?/g, '.');
        const searchRegex = new RegExp(searchPattern, 'i');
        
        switch (key) {
          case 'name':
            const name = (item.fullName || item.email).toLowerCase();
            if (!searchRegex.test(name)) return false;
            break;
          case 'email':
            if (!searchRegex.test(item.email.toLowerCase())) return false;
            break;
          case 'phone':
            const phone = item.phoneCountry && item.phone ? 
              `+${item.phoneCountry}${item.phone}`.toLowerCase() : '';
            if (!searchRegex.test(phone)) return false;
            break;
          case 'role':
            if (!searchRegex.test(item.permissionsId.toLowerCase())) return false;
            break;
          case 'status':
            const status = UserStates[item.userStateId]?.label || item.userStateId;
            if (!searchRegex.test(status.toLowerCase())) return false;
            break;
          default:
            break;
        }
      }

      return true;
    });
  };

  const ColumnHeader = ({ column, title }) => {
    const handleSort = () => {
      setSortConfig(prevConfig => ({
        key: column,
        direction: 
          prevConfig.key === column 
            ? prevConfig.direction === 'asc' 
              ? 'desc' 
              : 'asc'
            : 'asc'
      }));
    };

    return (
      <th 
        onClick={handleSort}
        className="px-6 py-4 text-left text-sm font-medium text-[var(--text-secondary)] cursor-pointer select-none"
      >
        <div className="flex items-center gap-2 group">
          <span>{title}</span>
          <div className="flex items-center">
            {sortConfig.key === column ? (
              sortConfig.direction === 'asc' ? 
                <ChevronUp className="w-4 h-4" /> : 
                <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronUp className="w-4 h-4 opacity-0 group-hover:opacity-50" />
            )}
          </div>
        </div>
      </th>
    );
  };

  const handleChangeRole = async () => {
    if (!selectedUser || changingRole) return;
    setChangingRole(true);
    setError('');

    try {
      const token = getToken();
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${API_BASE}/company/users/${selectedUser.id}/role`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          permissionsId: selectedRole
        })
      });

      if (response.ok) {
        setSuccess('User role updated successfully');
        setTimeout(() => {
          setSuccess('');
        }, 3000);
        fetchUsers();
        setShowChangeRoleModal(false);
        setSelectedUser(null);
        setSelectedRole('User');
      } else {
        const data = await response.json();
        throw new Error(data.message || 'Failed to update user role');
      }
    } catch (err) {
      setError(err.message || 'Failed to update user role');
    } finally {
      setChangingRole(false);
    }
  };

  const handleRemoveUser = async () => {
    if (!selectedUser || removingUser || !isRemoveConfirmed) return;

    setRemovingUser(true);
    setError('');

    try {
      const token = getToken();
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${API_BASE}/company/users/${selectedUser.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setSuccess('User removed successfully');
        setTimeout(() => {
          setSuccess('');
        }, 3000);
        fetchUsers();
        setShowRemoveUserModal(false);
        setSelectedUser(null);
        setIsRemoveConfirmed(false);
      } else {
        const data = await response.json();
        throw new Error(data.message || 'Failed to remove user');
      }
    } catch (err) {
      setError(err.message || 'Failed to remove user');
    } finally {
      setRemovingUser(false);
    }
  };

  // Add useEffect for dropdown positioning
  useEffect(() => {
    if (actionMenuOpen) {
      const button = actionMenuRef.current?.querySelector('button');
      const dropdown = actionMenuRef.current?.querySelector('[role="menu"]');
      if (button && dropdown) {
        const buttonRect = button.getBoundingClientRect();
        const spaceBelow = window.innerHeight - buttonRect.bottom;
        const dropdownHeight = dropdown.offsetHeight;

        if (spaceBelow < dropdownHeight + 10) { // Add 10px buffer
          setDropdownPosition('top');
        } else {
          setDropdownPosition('bottom');
        }
      }
    }
  }, [actionMenuOpen]);

  // Add a function to calculate position safely
  const calculateMenuPosition = (rect) => {
    const leftPosition = Math.max(20, Math.min(rect.left - 150, window.innerWidth - 220)); // Ensure menu stays within viewport
    return {
      top: rect.bottom + 5,
      left: leftPosition
    };
  };

  const handleChangeTeam = async () => {
    if (!selectedUser || changingTeam) return;
    
    // Check if user entered a team name but didn't press Add
    if (showCreateUserTeamInput && newUserTeamName.trim() !== '') {
      setUserTeamError('Please click "Add" to add this team before updating');
      return;
    }
    
    setChangingTeam(true);
    setError('');
    setSuccess('');

    try {
      const token = getToken();
      if (!token) {
        navigate('/login');
        return;
      }

      let teamIdToUse = selectedUserTeam;
      let newTeamCreated = false;
      let newTeam = null; // Define the newTeam variable at the top level
      
      // Handle "No Team" case - only when explicitly selecting 'noTeam'
      if (teamIdToUse === 'noTeam') {
        // Instead of throwing an error, treat it as "No Team" selection
        teamIdToUse = 'noTeam';
        
        // Use DELETE method to remove user from team
        const response = await fetch(`${API_BASE}/company/users/${selectedUser.id}/team`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          setSuccess('User removed from team successfully');
          setTimeout(() => {
            setSuccess('');
          }, 3000);
          fetchUsers(); // Refresh users list
          setShowChangeTeamModal(false);
          setSelectedUser(null);
          setSelectedUserTeam(null);
          setShowCreateUserTeamInput(false);
          setNewUserTeamName('');
          setUserTeamError('');
          
          setChangingTeam(false); // Reset the changing state
          return; // Exit early since we've handled the case
        } else {
          const data = await response.json().catch(() => ({ message: 'Unknown error' }));
          throw new Error(data.message || 'Failed to remove user from team');
        }
      }
      
      // Check if there's actually a team selected (not null or empty)
      if (teamIdToUse === null || teamIdToUse === '') {
        // Instead of throwing an error, treat it as "No Team" selection
        teamIdToUse = 'noTeam';
        
        // Use DELETE method to remove user from team
        const response = await fetch(`${API_BASE}/company/users/${selectedUser.id}/team`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          setSuccess('User removed from team successfully');
          setTimeout(() => {
            setSuccess('');
          }, 3000);
          fetchUsers(); // Refresh users list
          setShowChangeTeamModal(false);
          setSelectedUser(null);
          setSelectedUserTeam(null);
          setShowCreateUserTeamInput(false);
          setNewUserTeamName('');
          setUserTeamError('');
          
          setChangingTeam(false); // Reset the changing state
          return; // Exit early since we've handled the case
        } else {
          const data = await response.json().catch(() => ({ message: 'Unknown error' }));
          throw new Error(data.message || 'Failed to remove user from team');
        }
      }
      
      // STEP 1: Create the team if needed
      // If we get here, we're assigning a team (not removing), so find the team object
      const selectedTeamObj = teams.find(t => {
        // Removed console.log
        // Removed console.log
        return t.id.toString() === teamIdToUse?.toString();
      });
      
      // Removed console.log
      
      // Only create the team if it's temporary and it's actually selected
      if (teamIdToUse && teamIdToUse !== 'noTeam') {
        const isTemporaryTeam = selectedTeamObj?.isTemporary;
        // Removed console.log
        
        // If it's a temporary team, create it first
        if (isTemporaryTeam || showCreateUserTeamInput) {
          try {
            // Get the team name to create
            const teamNameToCreate = isTemporaryTeam ? 
              selectedTeamObj.name : 
              newUserTeamName.trim();
            
            // Removed console.log
            
            // Skip if no name provided
            if (!teamNameToCreate) {
              throw new Error('Team name is required');
            }
            
            // Capitalize first letter of each word
            const formattedTeamName = formatTeamName(teamNameToCreate);
            // Removed console.log

            // Create the team
            // Removed console.log
            setSuccess('Creating team...');
            
            const teamResponse = await fetch(`${API_BASE}/account/teams`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                name: formattedTeamName
              })
            });
            
            if (teamResponse.ok) {
              // Successfully created the team
              try {
                const responseText = await teamResponse.text();
                newTeam = responseText ? JSON.parse(responseText) : { id: null, name: formattedTeamName };
              } catch (err) {
                newTeam = { id: null, name: formattedTeamName };
              }
              
              // Update the teams list with the new team
              setTeams(prev => [...prev, newTeam]);
              // Use the new team ID (ensuring it's not null)
              if (newTeam && newTeam.id) {
                teamIdToUse = newTeam.id;
                // Removed console.log
              } else {
                // Removed console.log
              }
              newTeamCreated = true;
              setSuccess('Team created successfully. Updating user...');
            } else {
              // Server returned an error
              const errorData = await teamResponse.json().catch(() => ({ message: 'Unknown error' }));
              throw new Error(errorData.message || 'Failed to create team');
            }
          } catch (err) {
            setUserTeamError(err.message || 'Failed to create team');
            setChangingTeam(false);
            return;
          }
        }
      }

      // STEP 2: Update the user's team assignment
      // This code shouldn't execute due to early return, but keeping as a safeguard
      if (teamIdToUse === 'noTeam') {
        // Use DELETE method to remove user from team
        const response = await fetch(`${API_BASE}/company/users/${selectedUser.id}/team`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          setSuccess('User removed from team successfully');
          setTimeout(() => {
            setSuccess('');
          }, 3000);
          fetchUsers(); // Refresh users list
          setShowChangeTeamModal(false);
          setSelectedUser(null);
          setSelectedUserTeam(null);
          setShowCreateUserTeamInput(false);
          setNewUserTeamName('');
          setUserTeamError('');
        } else {
          const data = await response.json();
          throw new Error(data.message || 'Failed to remove user from team');
        }
      } else {
        // Get the team name for API - use newTeam if we just created it
        let teamNameForApi;

        if (newTeamCreated && newTeam) {
          // Use the newly created team directly
          teamNameForApi = formatTeamName(newTeam.name);
          // Removed console.log
        } else {
          // Find the team in the existing teams list
          const existingTeam = teams.find(t => t.id.toString() === teamIdToUse?.toString());
          if (!existingTeam) {
            // Instead of throwing an error, treat it as "No Team" selection
            // Use DELETE method to remove user from team
            const response = await fetch(`${API_BASE}/company/users/${selectedUser.id}/team`, {
              method: 'DELETE',
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });

            if (response.ok) {
              setSuccess('User removed from team successfully');
              setTimeout(() => {
                setSuccess('');
              }, 3000);
              fetchUsers(); // Refresh users list
              setShowChangeTeamModal(false);
              setSelectedUser(null);
              setSelectedUserTeam(null);
              setShowCreateUserTeamInput(false);
              setNewUserTeamName('');
              setUserTeamError('');
              
              setChangingTeam(false); // Reset the changing state
              return; // Exit early since we've handled the case
            } else {
              const data = await response.json().catch(() => ({ message: 'Unknown error' }));
              throw new Error(data.message || 'Failed to remove user from team');
            }
          }
          teamNameForApi = formatTeamName(existingTeam.name);
        }

        console.log('Sending team assignment request with name:', teamNameForApi);
        
        const response = await fetch(`${API_BASE}/company/users/${selectedUser.id}/team`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            teamName: teamNameForApi
          })
        });

        console.log('Team assignment API response status:', response.status);

        if (response.ok) {
          setSuccess(newTeamCreated ? 'Team created and assigned successfully' : 'Team assigned successfully');
          setTimeout(() => {
            setSuccess('');
          }, 3000);
          fetchUsers(); // Refresh users list
          setShowChangeTeamModal(false);
          setSelectedUser(null);
          setSelectedUserTeam(null);
          setShowCreateUserTeamInput(false);
          setNewUserTeamName('');
          setUserTeamError('');
        } else {
          const data = await response.json();
          throw new Error(data.message || 'Failed to update team');
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to update team');
    } finally {
      setChangingTeam(false);
    }
  };

  const handleRefresh = () => {
    fetchUsers();
    // Teams data is now automatically fetched by fetchUsers
  };

  if (loading) {
    return <LoadingScreen />;
  }

  const processedUsers = sortData(filterData(users));

  return (
    <div className="relative w-full h-screen overflow-y-auto bg-gradient-to-b from-[var(--bg-start)] to-[var(--bg-end)] custom-scrollbar">
      <style>{animationStyle}</style>
      <style>
        {`
          /* Custom scrollbar styling */
          .custom-scrollbar::-webkit-scrollbar {
            width: 8px;
          }
          
          .custom-scrollbar::-webkit-scrollbar-track {
            background: var(--bg-start);
            border-radius: 10px;
          }
          
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: rgba(94, 53, 177, 0.2); /* Using the accent primary color with low opacity */
            border-radius: 10px;
            border: 2px solid var(--bg-start);
            transition: all 0.2s ease;
          }
          
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: rgba(94, 53, 177, 0.3);
          }
          
          @media (max-width: 640px) {
            .custom-scrollbar::-webkit-scrollbar {
              width: 4px;
            }
            
            .custom-scrollbar::-webkit-scrollbar-thumb {
              border: 1px solid var(--bg-start);
            }
          }
          
          /* Firefox support */
          .custom-scrollbar {
            scrollbar-width: thin;
            scrollbar-color: rgba(94, 53, 177, 0.2) transparent;
          }
        `}
      </style>
      <div className="max-w-6xl mx-auto p-2 sm:p-3 pb-6">
        <div className="mb-4 sm:mb-6">
          <div className="flex justify-between items-center mb-3">
            <h1 className="text-xl sm:text-2xl font-bold text-[var(--text-primary)]">Manage Users</h1>
            <button
              onClick={() => setShowInviteModal(true)}
              className="flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-[var(--accent-primary)] text-white rounded-md hover:bg-[var(--hover-dark)] transition-colors"
            >
              <UserPlus className="w-6 h-6 text-white" strokeWidth={1.5} />
              <span>Invite User</span>
            </button>
          </div>
          <div className="flex justify-between items-center mb-3">
            <div className="hidden sm:flex items-center gap-2 w-full max-w-3xl">
              <div className="relative flex-grow">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-[var(--text-secondary)]" />
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name or email..."
                  className="w-full pl-10 pr-10 py-2 bg-[var(--card-bg)] border border-[var(--border-color)] rounded-md focus:outline-none focus:ring-1 focus:ring-[var(--accent-primary)] shadow-md"
                />
                {searchQuery && (
                  <button 
                    onClick={() => setSearchQuery('')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                  >
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>
              
              {/* Filter by team icon button */}
              <div className="relative" ref={desktopTeamFilterRef}>
                <button
                  onClick={() => setShowDesktopTeamFilter(!showDesktopTeamFilter)}
                  className={`p-2 rounded-md transition-colors ${
                    teamFilter !== 'all'
                      ? 'bg-[var(--accent-primary)] text-white shadow-md ring-2 ring-[var(--accent-primary)]'
                      : 'text-[var(--text-secondary)] hover:bg-[var(--hover-light)]'
                  }`}
                  title={teamFilter === 'all' ? 'Filter by team' : `Filtered by: ${teamFilter === 'noTeam' ? 'No Team' : teams.find(t => t.id.toString() === teamFilter)?.name || ''}`}
                >
                  <Filter className={`w-5 h-5 ${teamFilter !== 'all' ? 'stroke-[2px]' : ''}`} />
                </button>
                
                {showDesktopTeamFilter && (
                  <div className="absolute right-0 mt-1 w-52 rounded-md overflow-hidden shadow-lg bg-[var(--card-bg)] border border-[var(--border-color)] z-20">
                    <div className="p-2 border-b border-[var(--border-color)] bg-[var(--card-bg)] bg-opacity-90">
                      <p className="text-xs font-medium text-[var(--text-secondary)]">Filter by Team</p>
                    </div>
                    <div className="max-h-60 overflow-y-auto py-1">
                      <button
                        onClick={() => {
                          setTeamFilter('all');
                          setShowDesktopTeamFilter(false);
                        }}
                        className={`block w-full text-left px-4 py-2 text-sm ${
                          teamFilter === 'all'
                            ? 'bg-[var(--accent-primary)] bg-opacity-20 font-semibold text-white'
                            : 'text-[var(--text-primary)] hover:bg-[var(--hover-light)]'
                        }`}
                      >
                        All Teams
                      </button>
                      <button
                        onClick={() => {
                          setTeamFilter('noTeam');
                          setShowDesktopTeamFilter(false);
                        }}
                        className={`block w-full text-left px-4 py-2 text-sm ${
                          teamFilter === 'noTeam'
                            ? 'bg-[var(--accent-primary)] bg-opacity-20 font-semibold text-white'
                            : 'text-[var(--text-primary)] hover:bg-[var(--hover-light)]'
                        }`}
                      >
                        No Team
                      </button>
                      {teams.map(team => (
                        <button
                          key={team.id}
                          onClick={() => {
                            setTeamFilter(team.id.toString());
                            setShowDesktopTeamFilter(false);
                          }}
                          className={`block w-full text-left px-4 py-2 text-sm ${
                            teamFilter === team.id.toString()
                              ? 'bg-[var(--accent-primary)] bg-opacity-20 font-semibold text-white'
                              : 'text-[var(--text-primary)] hover:bg-[var(--hover-light)]'
                          }`}
                        >
                          {team.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="hidden sm:flex items-center gap-2 px-3 py-1.5 text-[var(--text-secondary)] hover:bg-[var(--hover-light)] rounded-md transition-colors disabled:opacity-50 ml-2"
              title="Refresh users list"
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-3 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-3 p-3 bg-green-100 border border-green-400 text-green-700 rounded flex items-center gap-2 animate-[fadeIn_0.3s_ease-in-out]">
            <CheckCircle2 className="w-5 h-5 text-green-500" />
            {success}
          </div>
        )}

        <div className="bg-[var(--card-bg)] rounded-lg border border-[var(--border-color)] overflow-visible">
          {/* Mobile View */}
          <div className="block sm:hidden">
            <div className="px-3 py-2 border-b border-[var(--border-color)] bg-[var(--card-bg)] bg-opacity-50">
              <div className="flex items-center justify-between gap-2 mb-2">
                <button
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className="p-1.5 text-[var(--text-secondary)] hover:bg-[var(--hover-light)] rounded-full transition-colors disabled:opacity-50"
                  aria-label="Refresh users list"
                >
                  <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                </button>
                <div className="flex items-center gap-2">
                  {/* Filter button placed next to sort */}
                  <div className="relative" ref={mobileTeamFilterRef}>
                    <button
                      onClick={() => {
                        setShowMobileTeamFilter(!showMobileTeamFilter);
                      }}
                      className={`p-1.5 rounded-md transition-colors ${
                        teamFilter !== 'all'
                          ? 'bg-[var(--accent-primary)] text-white shadow-md ring-2 ring-[var(--accent-primary)]'
                          : 'text-[var(--text-secondary)] hover:bg-[var(--hover-light)]'
                      }`}
                      title="Filter by team"
                    >
                      <Filter className={`w-4 h-4 ${teamFilter !== 'all' ? 'stroke-[2px]' : ''}`} />
                    </button>
                    
                    {showMobileTeamFilter && (
                      <div className="absolute right-0 mt-1 w-48 rounded-md overflow-hidden shadow-lg bg-[var(--card-bg)] border border-[var(--border-color)] z-20">
                        <div className="p-2 border-b border-[var(--border-color)] bg-[var(--card-bg)] bg-opacity-90 flex items-center">
                          <p className="text-xs font-medium text-[var(--text-secondary)]">Filter by Team</p>
                          {loadingTeams && (
                            <div className="ml-auto">
                              <RefreshCw className="w-3 h-3 text-[var(--text-secondary)] animate-spin" />
                            </div>
                          )}
                        </div>
                        <div className="max-h-60 overflow-y-auto py-1">
                          <button
                            onClick={() => {
                              setTeamFilter('all');
                              setShowMobileTeamFilter(false);
                            }}
                            className={`block w-full text-left px-4 py-2 text-sm ${
                              teamFilter === 'all'
                                ? 'bg-[var(--accent-primary)] bg-opacity-20 font-semibold text-white'
                                : 'text-[var(--text-primary)] hover:bg-[var(--hover-light)]'
                            }`}
                          >
                            All Teams
                          </button>
                                                      <button
                              onClick={() => {
                                setTeamFilter('noTeam');
                                setShowMobileTeamFilter(false);
                              }}
                            className={`block w-full text-left px-4 py-2 text-sm ${
                              teamFilter === 'noTeam'
                                ? 'bg-[var(--accent-primary)] bg-opacity-20 font-semibold text-white'
                                : 'text-[var(--text-primary)] hover:bg-[var(--hover-light)]'
                            }`}
                          >
                            No Team
                          </button>
                          {teams.length === 0 && !loadingTeams ? (
                            <div className="px-4 py-2 text-sm text-[var(--text-secondary)] italic text-center">
                              No teams found
                            </div>
                          ) : (
                            teams.map(team => (
                                                              <button
                                  key={team.id}
                                  onClick={() => {
                                    setTeamFilter(team.id.toString());
                                    setShowMobileTeamFilter(false);
                                  }}
                                                                                                  className={`block w-full text-left px-4 py-2 text-sm ${
                                  teamFilter === team.id.toString()
                                    ? 'bg-[var(--accent-primary)] bg-opacity-20 font-semibold text-white'
                                    : 'text-[var(--text-primary)] hover:bg-[var(--hover-light)]'
                                }`}
                              >
                                {team.name}
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <button
                    onClick={() => setShowSortModal(true)}
                    className="p-1.5 text-[var(--text-secondary)] hover:bg-[var(--hover-light)] rounded-full transition-colors"
                    aria-label="Sort users"
                  >
                    {sortConfig.direction === 'asc' ? <SortAsc className="w-5 h-5" /> : <SortDesc className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              
              {/* Mobile search input */}
              <div className="flex flex-col space-y-2">
                <div className="relative w-full mt-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-4 w-4 text-[var(--text-secondary)]" />
                  </div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search name or email..."
                    className="w-full pl-9 pr-9 py-1.5 text-sm bg-[var(--card-bg)] text-[var(--text-primary)] border border-[var(--border-color)] rounded-md focus:outline-none focus:ring-1 focus:ring-[var(--accent-primary)] shadow-sm"
                  />
                  {searchQuery && (
                    <button 
                      onClick={() => setSearchQuery('')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                

              </div>
            </div>
            
            {processedUsers.length === 0 ? (
              <div className="p-8 text-center">
                <div className="flex flex-col items-center justify-center py-8">
                  {refreshing ? (
                    // Show loading spinner when refreshing data
                    <>
                      <RefreshCw className="w-12 h-12 text-[var(--accent-primary)] animate-spin mb-4" />
                      <h3 className="text-lg font-medium text-[var(--text-primary)] mb-2">Loading users...</h3>
                    </>
                  ) : (
                    // Show no users found message only when not refreshing
                    <>
                      <Search className="w-12 h-12 text-[var(--text-secondary)] opacity-30 mb-4" />
                      <h3 className="text-lg font-medium text-[var(--text-primary)] mb-2">No users found</h3>
                      <p className="text-[var(--text-secondary)] max-w-md mx-auto mb-4">
                        {searchQuery ? 
                          `No users match "${searchQuery}". Try a different search term or clear your search.` : 
                          "No users match your current filters."}
                      </p>
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery('')}
                          className="px-4 py-2 bg-[var(--accent-primary)] text-white rounded-md hover:bg-[var(--hover-dark)] transition-colors"
                        >
                          Clear Search
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            ) : (
              processedUsers.map(user => (
                <div key={user.id} className="border-b border-[var(--border-color)] p-3">
                  <div className="flex items-center gap-3">
                    <UserAvatar user={user} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-[var(--text-primary)] brightness-110 text-sm">
                          {user.fullName || user.email}
                        </span>
                      </div>
                      {user.userStateId.startsWith('Pending') && user.fullName && (
                        <div className="text-sm text-[var(--text-secondary)] truncate">
                          {user.email}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <div className="flex items-center">
                        {!user.userStateId.startsWith('Pending') && user.fullName && (
                          <button
                            onClick={() => {
                              setSelectedUser(user);
                              setShowContactModal(true);
                            }}
                            className="group relative p-2 text-[var(--text-secondary)] hover:bg-[var(--hover-light)] rounded-full transition-colors"
                          >
                            <Contact className="w-5 h-5" strokeWidth={1.5} />
                            <span className="absolute bottom-full right-0 mb-2 px-2 py-1 text-xs font-medium text-white bg-gray-800 rounded-md opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-[99999]">
                              View contact info
                            </span>
                          </button>
                        )}

                        {/* Red trash icon for pending users */}
                        {user.userStateId.startsWith('Pending') && (
                          <button
                            onClick={() => {
                              setSelectedInviteToCancel(user);
                              setShowConfirmCancelModal(true);
                            }}
                            className="p-2 text-red-400 hover:bg-red-50/50 rounded-full transition-colors"
                          >
                            <Trash2 className="w-5 h-5" strokeWidth={1.5} />
                          </button>
                        )}

                        {/* Mobile menu button */}
                        <div className="relative" style={{ position: 'static' }}>
                          <button
                            type="button"
                            data-menu-button
                            onClick={(e) => {
                              e.stopPropagation();
                              const rect = e.currentTarget.getBoundingClientRect();
                              const position = calculateMenuPosition(rect);
                              setButtonPosition(position);
                              setActionMenuOpen(actionMenuOpen === user.id ? null : user.id);
                            }}
                            className="p-2 rounded-full transition-colors text-[var(--text-secondary)] hover:bg-[var(--hover-light)]"
                          >
                            <MoreVertical className="w-5 h-5" />
                          </button>
                          
                          {actionMenuOpen === user.id && (
                            <div 
                              data-menu-content
                              className="fixed rounded-md shadow-lg bg-[var(--card-bg)] border border-[var(--border-color)] z-[99999] py-1 w-48"
                              style={{
                                top: `${buttonPosition.top}px`,
                                left: `${buttonPosition.left}px`,
                                filter: 'drop-shadow(0 10px 8px rgb(0 0 0 / 0.04)) drop-shadow(0 4px 3px rgb(0 0 0 / 0.1))'
                              }}
                            >
                              {user.permissionsId !== 'Owner' && user.id !== userData?.id && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedUser(user);
                                    setSelectedRole(user.permissionsId);
                                    setShowChangeRoleModal(true);
                                    setActionMenuOpen(null);
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-[var(--text-primary)] hover:bg-[var(--hover-light)] transition-colors flex items-center gap-2"
                                >
                                  <Shield className="w-4 h-4" />
                                  Change Role
                                </button>
                              )}
                              
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedUser(user);
                                  setSelectedUserTeam(user.teamId || (user.teamName ? user.teamId : ''));
                                  setShowChangeTeamModal(true);
                                  setActionMenuOpen(null);
                                  fetchTeams();
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-[var(--text-primary)] hover:bg-[var(--hover-light)] transition-colors flex items-center gap-2"
                              >
                                <Users className="w-4 h-4" />
                                {user.teamName ? 'Change Team' : 'Assign Team'}
                              </button>
                              
                              {!user.userStateId.startsWith('Pending') && user.permissionsId !== 'Owner' && user.id !== userData?.id && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedUser(user);
                                    setShowRemoveUserModal(true);
                                    setActionMenuOpen(null);
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-red-50/50 transition-colors flex items-center gap-2"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  Remove User
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {user.teamName && (
                        <div className="pr-2 mt-1">
                          <span className="inline-flex items-center px-2 py-1 text-xs font-medium border-l-2 border-blue-500 bg-blue-50 text-blue-600 transition-all duration-200 rounded-[2px] truncate max-w-[95%]">
                            <span className="mr-1 text-blue-500 font-semibold">@</span>
                            {user.teamName}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${Permissions[user.permissionsId]?.color || 'bg-gray-500 text-white'}`}>
                      {Permissions[user.permissionsId]?.label || user.permissionsId}
                    </span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${UserStates[user.userStateId]?.color || 'text-gray-500'}`}>
                      {UserStates[user.userStateId]?.label || user.userStateId}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Desktop View */}
          <div className="hidden sm:block">
            <div className="relative overflow-hidden bg-[var(--card-bg)] rounded-lg border border-[var(--border-color)]">
              {processedUsers.length > 0 ? (
                <>
                  {/* Grid Header */}
                  <div className="grid grid-cols-12 border-b border-[var(--border-color)] bg-[var(--card-bg)] sticky top-0 z-10 rounded-t-lg overflow-hidden w-full">
                    <div onClick={() => handleSort('name')} className="pl-6 pr-2 py-4 text-left col-span-3 cursor-pointer hover:bg-[var(--hover-light)] transition-colors rounded">
                      <div className="flex items-center gap-2 group">
                        <span className="text-sm font-medium text-[var(--text-secondary)]">User</span>
                        <SortIcon columnKey="name" />
                      </div>
                    </div>
                    {/* Other header columns */}
                    <div onClick={() => handleSort('email')} className="px-3 py-4 text-left col-span-2 cursor-pointer hover:bg-[var(--hover-light)] transition-colors rounded">
                      <div className="flex items-center gap-2 group">
                        <span className="text-sm font-medium text-[var(--text-secondary)]">Email</span>
                        <SortIcon columnKey="email" />
                      </div>
                    </div>
                    <div onClick={() => handleSort('phone')} className="px-3 py-4 text-left col-span-2 cursor-pointer hover:bg-[var(--hover-light)] transition-colors rounded">
                      <div className="flex items-center gap-2 group">
                        <span className="text-sm font-medium text-[var(--text-secondary)]">Phone</span>
                        <SortIcon columnKey="phone" />
                      </div>
                    </div>
                    <div onClick={() => handleSort('role')} className="px-6 py-4 text-left col-span-1 cursor-pointer hover:bg-[var(--hover-light)] transition-colors rounded">
                      <div className="flex items-center gap-2 group">
                        <span className="text-sm font-medium text-[var(--text-secondary)]">Role</span>
                        <SortIcon columnKey="role" />
                      </div>
                    </div>
                    <div onClick={() => handleSort('status')} className="px-6 py-4 text-left col-span-1 cursor-pointer hover:bg-[var(--hover-light)] transition-colors rounded">
                      <div className="flex items-center gap-2 group">
                        <span className="text-sm font-medium text-[var(--text-secondary)]">Status</span>
                        <SortIcon columnKey="status" />
                      </div>
                    </div>
                    <div onClick={() => handleSort('teamName')} className="px-4 py-4 text-center col-span-2 cursor-pointer hover:bg-[var(--hover-light)] transition-colors rounded">
                      <div className="flex items-center justify-center gap-2 group mx-auto">
                        <span className="text-sm font-medium text-[var(--text-secondary)]">Team</span>
                        <SortIcon columnKey="teamName" />
                      </div>
                    </div>
                    <div className="px-6 py-4 text-left col-span-1 rounded">
                      <div className="flex items-center gap-2 group">
                        <span className="text-sm font-medium text-[var(--text-secondary)]">Actions</span>
                      </div>
                    </div>
                  </div>

                  {/* Grid Rows */}
                  <div className="divide-y divide-[var(--border-color)]">
                    {processedUsers.map((user, index) => (
                      <div 
                        key={user.id} 
                        className={`grid grid-cols-12 bg-[var(--background)] bg-opacity-50 
                          ${index === processedUsers.length - 1 ? 'rounded-b-lg overflow-hidden' : ''}`}
                      >
                                                <div className="pl-6 pr-2 py-4 col-span-3 flex items-center overflow-visible">
                          <div className="flex items-center gap-3 w-full">
                            <div className="flex-shrink-0">
                              <UserAvatar user={user} />
                            </div>
                            <div className="min-w-0 w-full">
                              <div className="font-medium text-[var(--text-primary)] brightness-110 text-sm break-normal">
                                {user.fullName || user.email}
                              </div>
                              {user.userStateId.startsWith('Pending') && user.fullName && (
                                <div className="text-sm text-[var(--text-secondary)] truncate">
                                  {user.email}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="px-3 py-4 col-span-2 flex items-center">
                          <span className="text-[var(--text-primary)] font-normal brightness-110 text-sm truncate">
                            {user.email}
                          </span>
                        </div>
                        <div className="px-3 py-4 col-span-2 flex items-center">
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-[var(--text-secondary)] flex-shrink-0" />
                            <span className="text-[var(--text-primary)] font-normal brightness-110 text-sm truncate">
                              {user.phoneCountry && user.phone ? (
                                `+${user.phoneCountry} ${user.phone}`
                              ) : (
                                <span className="text-[var(--text-secondary)] italic">Not set</span>
                              )}
                            </span>
                          </div>
                        </div>
                        <div className="px-6 py-4 col-span-1 flex items-center">
                          <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded text-xs font-medium ${Permissions[user.permissionsId]?.color || 'bg-gray-500 text-white'}`}>
                            {Permissions[user.permissionsId]?.label || user.permissionsId}
                          </span>
                        </div>
                        <div className="px-6 py-4 col-span-1 flex items-center">
                          <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded text-xs font-medium ${UserStates[user.userStateId]?.color || 'text-gray-500'}`}>
                            {UserStates[user.userStateId]?.label || user.userStateId}
                          </span>
                        </div>
                        <div className="px-2 py-4 flex items-center justify-center col-span-2">
                          {user.teamName && (
                            <span className="inline-flex items-center px-2 py-1 text-xs font-medium border-l-2 border-blue-500 bg-blue-50 text-blue-600 transition-all duration-200 rounded-[2px] truncate max-w-[95%]">
                              <span className="mr-1 text-blue-500 font-semibold">@</span>
                              {user.teamName}
                            </span>
                          )}
                        </div>
                        <div className="px-6 py-4 text-right col-span-1 flex items-center justify-end">
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1">
                              {/* Contact info button for all active users */}
                              {!user.userStateId.startsWith('Pending') && user.fullName && (
                                <button
                                  onClick={() => {
                                    setSelectedUser(user);
                                    setShowContactModal(true);
                                  }}
                                  className="p-1.5 text-[var(--text-secondary)] hover:bg-[var(--hover-light)] rounded-full transition-colors"
                                >
                                  <Contact className="w-5 h-5" strokeWidth={1.5} />
                                </button>
                              )}

                              {/* Red trash icon for pending users */}
                              {user.userStateId.startsWith('Pending') && (
                                <button
                                  onClick={() => {
                                    setSelectedInviteToCancel(user);
                                    setShowConfirmCancelModal(true);
                                  }}
                                  className="p-1.5 text-red-400 hover:bg-red-50/50 rounded-full transition-colors"
                                >
                                  <Trash2 className="w-5 h-5" strokeWidth={1.5} />
                                </button>
                              )}
                              
                              {/* Desktop View Three Dots Menu */}
                              <div className="relative" style={{ position: 'static' }}>
                                <button
                                  type="button"
                                  data-menu-button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    const position = calculateMenuPosition(rect);
                                    setButtonPosition(position);
                                    setActionMenuOpen(actionMenuOpen === user.id ? null : user.id);
                                  }}
                                  className="p-1.5 rounded-full transition-colors text-[var(--text-secondary)] hover:bg-[var(--hover-light)]"
                                >
                                  <MoreVertical className="w-5 h-5" />
                                </button>
                                
                                {actionMenuOpen === user.id && (
                                  <div 
                                    data-menu-content
                                    className="fixed rounded-md shadow-lg bg-[var(--card-bg)] border border-[var(--border-color)] z-[99999] py-1 w-48"
                                    style={{
                                      top: `${buttonPosition.top}px`,
                                      left: `${buttonPosition.left}px`,
                                      filter: 'drop-shadow(0 10px 8px rgb(0 0 0 / 0.04)) drop-shadow(0 4px 3px rgb(0 0 0 / 0.1))'
                                    }}
                                  >
                                    {user.permissionsId !== 'Owner' && user.id !== userData?.id && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedUser(user);
                                          setSelectedRole(user.permissionsId);
                                          setShowChangeRoleModal(true);
                                          setActionMenuOpen(null);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm text-[var(--text-primary)] hover:bg-[var(--hover-light)] transition-colors flex items-center gap-2"
                                      >
                                        <Shield className="w-4 h-4" />
                                        Change Role
                                      </button>
                                    )}
                                    
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedUser(user);
                                        setSelectedUserTeam(user.teamId || (user.teamName ? user.teamId : ''));
                                        setShowChangeTeamModal(true);
                                        setActionMenuOpen(null);
                                        fetchTeams();
                                      }}
                                      className="w-full text-left px-4 py-2 text-sm text-[var(--text-primary)] hover:bg-[var(--hover-light)] transition-colors flex items-center gap-2"
                                    >
                                      <Users className="w-4 h-4" />
                                      {user.teamName ? 'Change Team' : 'Assign Team'}
                                    </button>
                                    
                                    {!user.userStateId.startsWith('Pending') && user.permissionsId !== 'Owner' && user.id !== userData?.id && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedUser(user);
                                          setShowRemoveUserModal(true);
                                          setActionMenuOpen(null);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-red-50/50 transition-colors flex items-center gap-2"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                        Remove User
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="p-8 text-center">
                  <div className="flex flex-col items-center justify-center py-8">
                    {refreshing ? (
                      // Show loading spinner when refreshing data
                      <>
                        <RefreshCw className="w-12 h-12 text-[var(--accent-primary)] animate-spin mb-4" />
                        <h3 className="text-lg font-medium text-[var(--text-primary)] mb-2">Loading users...</h3>
                      </>
                    ) : (
                      // Show no users found message only when not refreshing
                      <>
                        <Search className="w-12 h-12 text-[var(--text-secondary)] opacity-30 mb-4" />
                        <h3 className="text-lg font-medium text-[var(--text-primary)] mb-2">No users found</h3>
                        <p className="text-[var(--text-secondary)] max-w-md mx-auto mb-4">
                          {searchQuery ? 
                            `No users match "${searchQuery}". Try a different search term or clear your search.` : 
                            "No users match your current filters."}
                        </p>
                        {searchQuery && (
                          <button
                            onClick={() => setSearchQuery('')}
                            className="px-4 py-2 bg-[var(--accent-primary)] text-white rounded-md hover:bg-[var(--hover-dark)] transition-colors"
                          >
                            Clear Search
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Bottom spacing to ensure all content is visible */}
      <div className="h-28"></div>

      {/* Contact Info Modal */}
      {showContactModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-[var(--card-bg)] p-6 rounded-lg max-w-md w-full mx-4">
            <div className="flex items-center gap-2 mb-6">
              <UserAvatar user={selectedUser} />
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-bold text-[var(--text-primary)]">
                    {selectedUser.fullName || <span className="italic text-[var(--text-secondary)]">No name set</span>}
                  </h2>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">Email</label>
                <div className="flex items-center gap-2 text-[var(--text-primary)]">
                  <Mail className="w-4 h-4 text-[var(--text-secondary)]" />
                  <span className="text-sm flex-1">{selectedUser.email}</span>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(selectedUser.email);
                    }}
                    className="p-1.5 text-[var(--text-secondary)] hover:bg-[var(--hover-light)] rounded-full transition-colors"
                    title="Copy email"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {selectedUser.phoneCountry && selectedUser.phone && (
                <div>
                  <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">Phone</label>
                  <div className="flex items-center gap-2 text-[var(--text-primary)]">
                    <Phone className="w-4 h-4 text-[var(--text-secondary)]" />
                    <span className="text-sm flex-1">+{selectedUser.phoneCountry} {selectedUser.phone}</span>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(`+${selectedUser.phoneCountry} ${selectedUser.phone}`);
                      }}
                      className="p-1.5 text-[var(--text-secondary)] hover:bg-[var(--hover-light)] rounded-full transition-colors"
                      title="Copy phone number"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
              
              {selectedUser.teamName && (
                <div>
                  <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">Team</label>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium border-l-2 border-blue-500 bg-blue-50 text-blue-600 transition-all duration-200 rounded-[2px]">
                      <span className="mr-1 text-blue-500 font-semibold">@</span>
                      {selectedUser.teamName}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-10 flex justify-end">
              <button
                onClick={() => {
                  setShowContactModal(false);
                  setSelectedUser(null);
                }}
                className="px-4 py-2 bg-[var(--accent-primary)] text-white rounded-md hover:bg-[var(--hover-dark)] transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <style>{animationStyle}</style>
          <div className="bg-[var(--card-bg)] p-6 rounded-lg max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-[var(--text-primary)] mb-4 flex items-center gap-2">
              <UserPlus className="w-6 h-6 text-white" strokeWidth={1.5} />
              Invite User
            </h2>
            
            {success ? (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4 animate-[bounce_0.5s_ease-in-out]">
                  <CheckCircle2 className="w-10 h-10 text-green-500 animate-[bounce_0.5s_ease-in-out]" />
                </div>
                <p className="text-green-600 text-lg font-medium text-center">{success}</p>
              </div>
            ) : (
              <form onSubmit={handleInvite}>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-[var(--text-primary)] mb-1">
                      Email Address
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-[var(--text-secondary)]" />
                      </div>
                      <input
                        type="email"
                        id="email"
                        value={inviteEmail}
                        onChange={(e) => {
                          setFormError('');
                          setInviteEmail(e.target.value);
                        }}
                        className={`w-full pl-10 pr-3 py-2 bg-[var(--background)] text-[var(--text-primary)] border ${formError ? 'border-red-500 ring-1 ring-red-500' : 'border-[var(--border-color)]'} rounded-md placeholder-[var(--text-secondary)] focus:outline-none focus:ring-1 ${formError ? 'focus:ring-red-500' : 'focus:ring-[var(--accent-primary)]'}`}
                        placeholder="user@example.com"
                        required
                        disabled={inviting}
                      />
                    </div>
                    {formError && (
                      <p className="mt-1 text-sm text-red-500">{formError}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-[var(--text-primary)] mb-1">
                      Name (optional)
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Users className="h-5 w-5 text-[var(--text-secondary)]" />
                      </div>
                      <input
                        type="text"
                        id="name"
                        value={inviteName}
                        onChange={(e) => setInviteName(e.target.value)}
                        className="w-full pl-10 pr-3 py-2 bg-[var(--background)] text-[var(--text-primary)] border border-[var(--border-color)] rounded-md placeholder-[var(--text-secondary)] focus:outline-none focus:ring-1 focus:ring-[var(--accent-primary)] shadow-sm placeholder-blue-400"
                        disabled={inviting}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                      Role
                    </label>
                    <div className="flex gap-3">
                      <label className="relative flex items-center group flex-1">
                        <input
                          type="radio"
                          name="role"
                          value="User"
                          checked={inviteRole === 'User'}
                          onChange={(e) => setInviteRole(e.target.value)}
                          className="sr-only peer"
                          disabled={inviting}
                        />
                        <div className="w-full px-4 py-2 rounded-md border peer-checked:bg-blue-500 peer-checked:text-white peer-checked:border-blue-500 border-[var(--border-color)] bg-[var(--card-bg)] text-[var(--text-secondary)] hover:bg-[var(--hover-light)] transition-colors cursor-pointer opacity-50 peer-checked:opacity-100">
                          User
                        </div>
                      </label>
                      <label className="relative flex items-center group flex-1">
                        <input
                          type="radio"
                          name="role"
                          value="Admin"
                          checked={inviteRole === 'Admin'}
                          onChange={(e) => setInviteRole(e.target.value)}
                          className="sr-only peer"
                          disabled={inviting}
                        />
                        <div className="w-full px-4 py-2 rounded-md border peer-checked:bg-purple-500 peer-checked:text-white peer-checked:border-purple-500 border-[var(--border-color)] bg-[var(--card-bg)] text-[var(--text-secondary)] hover:bg-[var(--hover-light)] transition-colors cursor-pointer opacity-50 peer-checked:opacity-100">
                          Admin
                        </div>
                      </label>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <label htmlFor="team" className="block text-sm font-medium text-[var(--text-primary)]">
                        Team Assignment
                      </label>
                      {loadingTeams && (
                        <div className="text-xs text-[var(--text-secondary)]">
                          <span className="inline-block h-4 w-4 border-2 border-t-transparent border-[var(--accent-primary)] rounded-full animate-spin mr-1"></span>
                          Loading...
                        </div>
                      )}
                    </div>
                    
                    <div className="space-y-4">
                      {/* Modern Team Selection Options */}
                      <div className="flex gap-2">
                        <div 
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!inviting) {
                              setSelectedTeamOption('noTeam');
                              setInviteTeam(null);
                              setShowCreateTeamInput(false);
                            }
                          }}
                          className={`flex-1 p-3 rounded-md border transition-all duration-200 cursor-pointer relative z-10 pointer-events-auto ${
                            selectedTeamOption === 'noTeam'
                              ? 'bg-[var(--card-bg)] border-[var(--accent-primary)] shadow-sm' 
                              : 'bg-[var(--card-bg)] border-[var(--border-color)] hover:bg-[var(--hover-light)]'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
                              selectedTeamOption === 'noTeam'
                                ? 'bg-[var(--accent-primary)] border-2 border-[var(--accent-primary)]' 
                                : 'border border-[var(--text-secondary)]'
                            }`}>
                              {selectedTeamOption === 'noTeam' && (
                                <div className="w-2 h-2 rounded-full bg-white"></div>
                              )}
                            </div>
                            <span className={`font-medium ${
                              selectedTeamOption === 'noTeam'
                                ? 'text-[var(--accent-primary)]' 
                                : 'text-[var(--text-secondary)]'
                            }`}>
                              No Team
                            </span>
                          </div>
                        </div>

                        <button 
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!inviting) {
                              setSelectedTeamOption('assignTeam');
                              setShowCreateTeamInput(false);
                            }
                          }}
                          className={`flex-1 p-3 rounded-md border transition-all duration-200 cursor-pointer relative z-20 hover:shadow-md ${
                            selectedTeamOption === 'assignTeam'
                              ? 'bg-gradient-to-br from-[var(--card-bg)] to-[var(--hover-light)] border-[var(--accent-primary)]' 
                              : 'bg-[var(--card-bg)] border-[var(--border-color)] hover:bg-[var(--hover-light)]'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
                              selectedTeamOption === 'assignTeam'
                                ? 'bg-[var(--accent-primary)] border-2 border-[var(--accent-primary)]' 
                                : 'border border-[var(--text-secondary)]'
                            }`}>
                              {selectedTeamOption === 'assignTeam' && (
                                <div className="w-2 h-2 rounded-full bg-white"></div>
                              )}
                            </div>
                            <span className={`font-medium ${
                              selectedTeamOption === 'assignTeam'
                                ? 'text-[var(--text-primary)]' 
                                : 'text-[var(--text-secondary)]'
                            }`}>
                              Assign Team
                            </span>
                          </div>
                        </button>
                      </div>

                      {/* Team Selection or Creation */}
                      {selectedTeamOption === 'assignTeam' && (
                        <div className={`mt-3 p-3 rounded-md border overflow-hidden transition-all duration-300 ${
                          selectedTeamOption === 'assignTeam'
                            ? 'bg-gradient-to-br from-[var(--card-bg)] to-[var(--hover-light)] border-[var(--accent-primary)]' 
                            : 'border-[var(--border-color)]'
                        }`}>
                          {showCreateTeamInput ? (
                            <div className="space-y-3">
                              <div className="flex items-center gap-2">
                                <input
                                  type="text"
                                  value={newTeamName}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setNewTeamName(value);
                                    // Only clear normal validation errors, but keep "click Add" errors
                                    if (teamError && !teamError.includes('Please click "Add"')) {
                                      setTeamError('');
                                    }
                                    if (value.trim()) {
                                      const formattedValue = formatTeamName(value);
                                      const teamNameExists = teams.some(team => 
                                        formatTeamName(team.name) === formattedValue
                                      );
                                      if (teamNameExists) {
                                        setTeamError('This team name already exists');
                                      }
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && newTeamName.trim() && !teamError) {
                                      e.preventDefault();
                                      // Create a truly unique temporary team ID
                                      const randomStr = Math.random().toString(36).substring(2, 10);
                                      const tempTeamId = `temp_${Date.now()}_${randomStr}`;
                                      
                                      // Format team name with first letter of each word capitalized
                                      const formattedTeamName = formatTeamName(newTeamName.trim());
                                      
                                      // Add this team to the local state without sending API request
                                      const tempTeam = {
                                        id: tempTeamId,
                                        name: formattedTeamName,
                                        isTemporary: true // Flag to identify this is a temp team
                                      };
                                      
                                      // Add to teams array (will be used to display the tag)
                                      setTeams(prev => [...prev, tempTeam]);
                                      
                                      // Set as selected team
                                      setInviteTeam(tempTeamId);
                                      
                                      // Reset the input and hide it
                                      setShowCreateTeamInput(false);
                                    }
                                  }}
                                  placeholder="Enter new team name"
                                  className="flex-1 px-3 py-2 bg-[var(--card-bg)] text-[var(--text-primary)] border border-[var(--border-color)] rounded-md focus:outline-none focus:ring-1 focus:ring-[var(--accent-primary)] shadow-sm placeholder-[var(--text-secondary)]"
                                  disabled={creatingTeam || inviting}
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (newTeamName.trim()) {
                                      // Clear all errors when adding the team
                                      setTeamError('');
                                      
                                      // Create a truly unique temporary team ID
                                      const randomStr = Math.random().toString(36).substring(2, 10);
                                      const tempTeamId = `temp_${Date.now()}_${randomStr}`;
                                      
                                      // Format team name with first letter of each word capitalized
                                      const formattedTeamName = formatTeamName(newTeamName.trim());
                                      
                                      // Add this team to the local state without sending API request
                                      const tempTeam = {
                                        id: tempTeamId,
                                        name: formattedTeamName,
                                        isTemporary: true // Flag to identify this is a temp team
                                      };
                                      
                                      // Add to teams array (will be used to display the tag)
                                      setTeams(prev => [...prev, tempTeam]);
                                      
                                      // Set as selected team
                                      setInviteTeam(tempTeamId);
                                      
                                      // Reset the input and hide it
                                      setShowCreateTeamInput(false);
                                    }
                                  }}
                                  disabled={!newTeamName.trim() || creatingTeam || inviting}
                                  className="px-3 py-2 bg-[var(--accent-primary)] text-white rounded-md hover:bg-[var(--hover-dark)] transition-colors disabled:opacity-50"
                                >
                                  Add
                                </button>
                              </div>
                              {teamError && (
                                <p className="text-sm text-red-400">{teamError}</p>
                              )}
                              <button
                                type="button"
                                onClick={() => {
                                  setShowCreateTeamInput(false);
                                  setNewTeamName('');
                                  setTeamError('');
                                  setInviteTeam(null);
                                }}
                                className="text-base text-[var(--text-primary)] hover:text-[var(--accent-primary)] transition-colors flex items-center gap-1"
                                disabled={creatingTeam || inviting}
                              >
                                ← Back to team selection
                              </button>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              <div className="flex flex-col">
                                                                  <div className="relative">
                                    <select
                                      id="team"
                                      value={inviteTeam || ''}
                                      onChange={(e) => {
                                        if (e.target.value) {
                                          setInviteTeam(e.target.value);
                                        }
                                      }}
                                      className="w-full appearance-none px-3 py-2 bg-[var(--card-bg)] text-[var(--text-primary)] border border-[var(--border-color)] rounded-md focus:outline-none focus:ring-1 focus:ring-[var(--accent-primary)] shadow-sm pr-8"
                                      style={{
                                        WebkitAppearance: 'none',
                                        MozAppearance: 'none'
                                      }}
                                      disabled={inviting || loadingTeams}
                                    >
                                      <option value="" disabled style={{ backgroundColor: 'var(--card-bg)', color: 'var(--text-secondary)' }}>-- Select a team --</option>
                                      {teams.map(team => (
                                        <option 
                                          key={team.id} 
                                          value={team.id} 
                                          style={{ backgroundColor: 'var(--card-bg)', color: 'var(--text-primary)' }}
                                        >
                                          {team.name}
                                        </option>
                                      ))}
                                    </select>
                                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                      <svg className="h-4 w-4 text-[var(--text-secondary)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                      </svg>
                                    </div>
                                  </div>
                                  
                                  <div className="flex justify-between items-center mt-2">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setShowCreateTeamInput(true);
                                        setNewTeamName('');
                                      }}
                                      className="text-base text-[var(--text-primary)] hover:text-[var(--accent-primary)] transition-colors flex items-center gap-1"
                                      disabled={inviting}
                                    >
                                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                      </svg>
                                      Create New Team
                                    </button>
                                  </div>
                              </div>
                              
                              {inviteTeam && (
                                <div className="mt-2 flex items-center">
                                  <div className="inline-flex items-center px-4 py-2 text-sm font-medium border-l-2 border-[var(--accent-primary)] bg-[var(--card-bg)] text-[var(--text-primary)] transition-all duration-200 hover:bg-[var(--hover-light)] rounded-md">
                                    <span className="mr-2 text-[var(--accent-primary)] font-semibold">@</span>
                                    {teams.find(t => t.id.toString() === inviteTeam.toString())?.name || ''}
                                                                      <button
                                    type="button"
                                    onClick={() => {
                                      // Check if this is a temporary team
                                      const tempTeam = teams.find(t => t.id.toString() === inviteTeam.toString());
                                      if (tempTeam && tempTeam.isTemporary) {
                                        // Remove from teams array only if it's a temporary team
                                        setTeams(prev => prev.filter(t => t.id.toString() !== inviteTeam.toString()));
                                      }
                                      // Just clear the selected team without removing existing teams
                                      setInviteTeam(null);
                                    }}
                                      className="ml-2 p-1.5 text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--hover-dark)] rounded-full transition-colors"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-8">
                  <button
                    type="button"
                    onClick={() => {
                      setShowInviteModal(false);
                      setInviteEmail('');
                      setInviteName('');
                      setInviteRole('User');
                      setInviteTeam(null);
                      setSelectedTeamOption('noTeam');
                      setShowCreateTeamInput(false);
                      setNewTeamName('');
                      setFormError('');
                      setSuccess('');
                    }}
                    className="px-4 py-2 text-[var(--text-primary)] hover:bg-[var(--hover-light)] rounded-md transition-colors"
                    disabled={inviting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={inviting}
                    className={`flex items-center justify-center gap-2 px-6 py-2 rounded-md text-white transition-all duration-200 ${
                      inviting ? 'bg-[var(--accent-primary)] opacity-75' : 'bg-[var(--accent-primary)] hover:bg-[var(--hover-dark)]'
                    }`}
                    style={{ minWidth: '120px' }}
                  >
                    {inviting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        <span>Send Invite</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Sort Modal */}
      {showSortModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
          <div className="bg-[var(--card-bg)] rounded-t-lg sm:rounded-lg w-full sm:max-w-xs mx-auto">
            <div className="p-4 border-b border-[var(--border-color)]">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-[var(--text-primary)]">Sort</h3>
                <button
                  onClick={() => {
                    setTempSortConfig(prev => ({
                      ...prev,
                      direction: prev.direction === 'asc' ? 'desc' : 'asc'
                    }));
                  }}
                  className="p-1.5 text-[var(--text-secondary)] hover:bg-[var(--hover-light)] rounded-full transition-colors"
                >
                  {tempSortConfig.direction === 'asc' ? <SortAsc className="w-5 h-5" /> : <SortDesc className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div className="p-2">
              <button
                key="sort-name"
                onClick={() => setTempSortConfig(prev => ({ ...prev, key: 'name' }))}
                className={`w-full text-left px-4 py-3 rounded-md hover:bg-[var(--hover-light)] transition-colors ${tempSortConfig.key === 'name' ? 'text-[var(--accent-primary)]' : 'text-[var(--text-primary)]'}`}
              >
                Name
              </button>
              <button
                key="sort-role"
                onClick={() => setTempSortConfig(prev => ({ ...prev, key: 'role' }))}
                className={`w-full text-left px-4 py-3 rounded-md hover:bg-[var(--hover-light)] transition-colors ${tempSortConfig.key === 'role' ? 'text-[var(--accent-primary)]' : 'text-[var(--text-primary)]'}`}
              >
                Role
              </button>
              <button
                key="sort-status"
                onClick={() => setTempSortConfig(prev => ({ ...prev, key: 'status' }))}
                className={`w-full text-left px-4 py-3 rounded-md hover:bg-[var(--hover-light)] transition-colors ${tempSortConfig.key === 'status' ? 'text-[var(--accent-primary)]' : 'text-[var(--text-primary)]'}`}
              >
                Status
              </button>
              <button
                key="sort-teamName"
                onClick={() => setTempSortConfig(prev => ({ ...prev, key: 'teamName' }))}
                className={`w-full text-left px-4 py-3 rounded-md hover:bg-[var(--hover-light)] transition-colors ${tempSortConfig.key === 'teamName' ? 'text-[var(--accent-primary)]' : 'text-[var(--text-primary)]'}`}
              >
                Team
              </button>
            </div>
            <div className="p-4 border-t border-[var(--border-color)] flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowSortModal(false);
                  setTempSortConfig(sortConfig); // Reset to current sort
                }}
                className="px-4 py-2 text-[var(--text-primary)] hover:bg-[var(--hover-light)] rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setSortConfig(tempSortConfig);
                  setShowSortModal(false);
                }}
                className="px-4 py-2 bg-[var(--accent-primary)] text-white rounded-md hover:bg-[var(--hover-dark)] transition-colors"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal for Cancel Invite */}
      {showConfirmCancelModal && selectedInviteToCancel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[var(--card-bg)] p-6 rounded-lg max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-[var(--text-primary)] mb-4">Cancel Invitation</h2>
            
            <p className="text-[var(--text-primary)] mb-6">
              Are you sure you want to cancel the invitation for{' '}
              <span className="font-semibold">
                {selectedInviteToCancel.fullName || selectedInviteToCancel.email}
              </span>?
            </p>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowConfirmCancelModal(false);
                  setSelectedInviteToCancel(null);
                }}
                className="px-4 py-2 text-[var(--text-primary)] hover:bg-[var(--hover-light)] rounded-md transition-colors"
              >
                No, keep it
              </button>
              <button
                onClick={() => handleCancelInvite(selectedInviteToCancel.email)}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
              >
                Yes, cancel invitation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Role Modal */}
      {showChangeRoleModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[var(--card-bg)] p-6 rounded-lg max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-[var(--text-primary)] mb-4">Change User Role</h2>
            
            <div className="mb-6">
              <p className="text-[var(--text-primary)] mb-4">
                Change role for{' '}
                <span className="font-semibold">
                  {selectedUser.fullName || selectedUser.email}
                </span>
              </p>

              <div>
                <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                  Role
                </label>
                <div className="flex gap-3">
                  <label className="relative flex items-center group flex-1">
                    <input
                      type="radio"
                      name="role"
                      value="User"
                      checked={selectedRole === 'User'}
                      onChange={(e) => setSelectedRole(e.target.value)}
                      className="sr-only peer"
                      disabled={changingRole}
                    />
                    <div className="w-full px-4 py-2 rounded-md border peer-checked:bg-blue-500 peer-checked:text-white peer-checked:border-blue-500 border-[var(--border-color)] bg-[var(--card-bg)] text-[var(--text-secondary)] hover:bg-[var(--hover-light)] transition-colors cursor-pointer opacity-50 peer-checked:opacity-100">
                      User
                    </div>
                  </label>
                  <label className="relative flex items-center group flex-1">
                    <input
                      type="radio"
                      name="role"
                      value="Admin"
                      checked={selectedRole === 'Admin'}
                      onChange={(e) => setSelectedRole(e.target.value)}
                      className="sr-only peer"
                      disabled={changingRole}
                    />
                    <div className="w-full px-4 py-2 rounded-md border peer-checked:bg-purple-500 peer-checked:text-white peer-checked:border-purple-500 border-[var(--border-color)] bg-[var(--card-bg)] text-[var(--text-secondary)] hover:bg-[var(--hover-light)] transition-colors cursor-pointer opacity-50 peer-checked:opacity-100">
                      Admin
                    </div>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowChangeRoleModal(false);
                  setSelectedUser(null);
                  setSelectedRole('User');
                }}
                className="px-4 py-2 text-[var(--text-primary)] hover:bg-[var(--hover-light)] rounded-md transition-colors"
                disabled={changingRole}
              >
                Cancel
              </button>
              <button
                onClick={handleChangeRole}
                disabled={changingRole || selectedRole === selectedUser.permissionsId}
                className={`flex items-center px-4 py-2 rounded-md text-white transition-colors ${
                  changingRole || selectedRole === selectedUser.permissionsId
                    ? 'bg-[var(--accent-primary)] opacity-50 cursor-not-allowed'
                    : 'bg-[var(--accent-primary)] hover:bg-[var(--hover-dark)]'
                }`}
              >
                {changingRole ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    <span>Updating...</span>
                  </>
                ) : (
                  'Update Role'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove User Modal */}
      {showRemoveUserModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[var(--card-bg)] p-6 rounded-lg max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-[var(--text-primary)] mb-4">Remove User</h2>
            
            <p className="text-[var(--text-primary)] mb-6">
              Are you sure you want to remove{' '}
              <span className="font-semibold">
                {selectedUser.fullName || selectedUser.email}
              </span>
              {' '}from the company?
            </p>

            <div className="mb-4">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  onChange={(e) => setIsRemoveConfirmed(e.target.checked)}
                  checked={isRemoveConfirmed}
                />
                <span className="ml-2 text-sm text-[var(--text-secondary)]">
                  I understand this action cannot be undone
                </span>
              </label>
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowRemoveUserModal(false);
                  setSelectedUser(null);
                  setIsRemoveConfirmed(false);
                }}
                className="px-4 py-2 text-[var(--text-primary)] hover:bg-[var(--hover-light)] rounded-md transition-colors"
                disabled={removingUser}
              >
                Cancel
              </button>
              <button
                onClick={handleRemoveUser}
                disabled={removingUser || !isRemoveConfirmed}
                className={`flex items-center px-4 py-2 rounded-md text-white transition-colors ${
                  removingUser || !isRemoveConfirmed
                    ? 'bg-red-500 opacity-50 cursor-not-allowed'
                    : 'bg-red-500 hover:bg-red-600'
                }`}
              >
                {removingUser ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    <span>Removing...</span>
                  </>
                ) : (
                  'Remove User'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Team Modal */}
      {showChangeTeamModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[var(--card-bg)] p-6 rounded-lg max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-[var(--text-primary)] mb-4">
              {selectedUser.teamName ? 'Change Team' : 'Assign Team'}
            </h2>
            
            <div className="mb-6">
              <p className="text-[var(--text-primary)] mb-4">
                {selectedUser.teamName ? 'Change' : 'Assign'} team for{' '}
                <span className="font-semibold">
                  {selectedUser.fullName || selectedUser.email}
                </span>
              </p>

              <div className="space-y-4">
                {/* Team Options */}
                <div className="flex gap-2">
                  <div 
                    onClick={() => {
                      if (!changingTeam) {
                        setSelectedUserTeam('noTeam');
                        setShowCreateUserTeamInput(false);
                      }
                    }}
                    className={`flex-1 p-3 rounded-md border transition-all duration-200 cursor-pointer relative z-10 ${
                      selectedUserTeam === 'noTeam'
                        ? 'bg-[var(--card-bg)] border-[var(--accent-primary)] shadow-sm' 
                        : 'bg-[var(--card-bg)] border-[var(--border-color)] hover:bg-[var(--hover-light)]'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
                        selectedUserTeam === 'noTeam'
                          ? 'bg-[var(--accent-primary)] border-2 border-[var(--accent-primary)]' 
                          : 'border border-[var(--text-secondary)]'
                      }`}>
                        {selectedUserTeam === 'noTeam' && (
                          <div className="w-2 h-2 rounded-full bg-white"></div>
                        )}
                      </div>
                      <span className={`font-medium ${
                        selectedUserTeam === 'noTeam'
                          ? 'text-[var(--accent-primary)]' 
                          : 'text-[var(--text-secondary)]'
                      }`}>
                        No Team
                      </span>
                    </div>
                  </div>

                  <div 
                    onClick={() => {
                      if (!changingTeam) {
                        if (selectedUserTeam === 'noTeam') {
                          setSelectedUserTeam(null);
                        }
                      }
                    }}
                    className={`flex-1 p-3 rounded-md border transition-all duration-200 cursor-pointer relative z-20 hover:shadow-md ${
                      selectedUserTeam !== 'noTeam'
                        ? 'bg-gradient-to-br from-[var(--card-bg)] to-[var(--hover-light)] border-[var(--accent-primary)]' 
                        : 'bg-[var(--card-bg)] border-[var(--border-color)] hover:bg-[var(--hover-light)]'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
                        selectedUserTeam !== 'noTeam'
                          ? 'bg-[var(--accent-primary)] border-2 border-[var(--accent-primary)]' 
                          : 'border border-[var(--text-secondary)]'
                      }`}>
                        {selectedUserTeam !== 'noTeam' && (
                          <div className="w-2 h-2 rounded-full bg-white"></div>
                        )}
                      </div>
                      <span className={`font-medium ${
                        selectedUserTeam !== 'noTeam'
                          ? 'text-[var(--text-primary)]' 
                          : 'text-[var(--text-secondary)]'
                      }`}>
                        Assign Team
                      </span>
                    </div>
                  </div>
                </div>

                {/* Team Selection Content */}
                {selectedUserTeam !== 'noTeam' && (
                  <div className="mt-3 p-3 rounded-md border bg-gradient-to-br from-[var(--card-bg)] to-[var(--hover-light)] border-[var(--accent-primary)]">
                    {showCreateUserTeamInput ? (
                      <div className="space-y-3">
                                                  <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={newUserTeamName}
                            onChange={(e) => {
                              const value = e.target.value;
                              setNewUserTeamName(value);
                              // Only clear normal validation errors, but keep "click Add" errors
                              if (userTeamError && !userTeamError.includes('Please click "Add"')) {
                                setUserTeamError('');
                              }
                              if (value.trim()) {
                                const formattedValue = formatTeamName(value);
                                const teamNameExists = teams.some(team => 
                                  formatTeamName(team.name) === formattedValue
                                );
                                if (teamNameExists) {
                                  setUserTeamError('This team name already exists');
                                }
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && newUserTeamName.trim() && !userTeamError) {
                                e.preventDefault();
                                // Create a truly unique temporary team ID
                                const randomStr = Math.random().toString(36).substring(2, 10);
                                const tempTeamId = `temp_${Date.now()}_${randomStr}`;
                                
                                // Format team name with first letter of each word capitalized
                                const formattedTeamName = formatTeamName(newUserTeamName.trim());
                                console.log('Creating temp team in modal with ID:', tempTeamId);
                                console.log('Temp team formatted name:', formattedTeamName);
                                
                                // Add this team to the local state without sending API request
                                const tempTeam = {
                                  id: tempTeamId,
                                  name: formattedTeamName,
                                  isTemporary: true // Flag to identify this is a temp team
                                };
                                
                                // Add to teams array (will be used to display the tag)
                                setTeams(prev => {
                                  console.log('Current teams before adding temp team:', prev);
                                  const updated = [...prev, tempTeam];
                                  console.log('Updated teams after adding temp team:', updated);
                                  return updated;
                                });
                                
                                // Set as selected team
                                setSelectedUserTeam(tempTeamId);
                                
                                // Reset the input and hide it
                                setShowCreateUserTeamInput(false);
                              }
                            }}
                            placeholder="Enter new team name"
                            className="flex-1 px-3 py-2 bg-[var(--card-bg)] text-[var(--text-primary)] border border-[var(--border-color)] rounded-md focus:outline-none focus:ring-1 focus:ring-[var(--accent-primary)] shadow-sm placeholder-[var(--text-secondary)]"
                            disabled={changingTeam}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              if (newUserTeamName.trim()) {
                                // Clear all errors when adding the team 
                                setUserTeamError('');
                                
                                // Create a truly unique temporary team ID
                                const randomStr = Math.random().toString(36).substring(2, 10);
                                const tempTeamId = `temp_${Date.now()}_${randomStr}`;
                                
                                // Format team name with first letter of each word capitalized
                                const formattedTeamName = formatTeamName(newUserTeamName.trim());
                                
                                // Add this team to the local state without sending API request
                                const tempTeam = {
                                  id: tempTeamId,
                                  name: formattedTeamName,
                                  isTemporary: true // Flag to identify this is a temp team
                                };
                                
                                // Add to teams array (will be used to display the tag)
                                setTeams(prev => [...prev, tempTeam]);
                                
                                // Set as selected team
                                setSelectedUserTeam(tempTeamId);
                                
                                // Reset the input and hide it
                                setShowCreateUserTeamInput(false);
                              }
                            }}
                            disabled={!newUserTeamName.trim() || changingTeam}
                            className="px-3 py-2 bg-[var(--accent-primary)] text-white rounded-md hover:bg-[var(--hover-dark)] transition-colors disabled:opacity-50"
                          >
                            Add
                          </button>
                        </div>
                        {userTeamError && (
                          <p className="text-sm text-red-400">{userTeamError}</p>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            setShowCreateUserTeamInput(false);
                            setNewUserTeamName('');
                            setUserTeamError('');
                          }}
                          className="text-base text-[var(--text-primary)] hover:text-[var(--accent-primary)] transition-colors flex items-center gap-1"
                          disabled={changingTeam}
                        >
                          ← Back to team selection
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="relative">
                          <select
                            id="userTeam"
                            value={selectedUserTeam || ''}
                            onChange={(e) => {
                              if (e.target.value) {
                                setSelectedUserTeam(e.target.value);
                              }
                            }}
                            className="w-full appearance-none px-3 py-2 bg-[var(--card-bg)] text-[var(--text-primary)] border border-[var(--border-color)] rounded-md focus:outline-none focus:ring-1 focus:ring-[var(--accent-primary)] shadow-sm pr-8"
                            style={{
                              WebkitAppearance: 'none',
                              MozAppearance: 'none'
                            }}
                            disabled={changingTeam}
                          >
                            <option value="" disabled style={{ backgroundColor: 'var(--card-bg)', color: 'var(--text-secondary)' }}>-- Select a team --</option>
                            {teams.map(team => (
                              <option 
                                key={team.id} 
                                value={team.id} 
                                style={{ backgroundColor: 'var(--card-bg)', color: 'var(--text-primary)' }}
                              >
                                {team.name}
                              </option>
                            ))}
                          </select>
                          <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                            <svg className="h-4 w-4 text-[var(--text-secondary)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>
                        
                        <div className="flex justify-between items-center mt-2">
                          <button
                            type="button"
                            onClick={() => {
                              setShowCreateUserTeamInput(true);
                              setNewUserTeamName('');
                            }}
                            className="text-base text-[var(--text-primary)] hover:text-[var(--accent-primary)] transition-colors flex items-center gap-1"
                            disabled={changingTeam}
                          >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            Create New Team
                          </button>
                        </div>
                        
                        {selectedUserTeam && selectedUserTeam !== 'noTeam' && (
                          <div className="mt-4 flex items-center">
                            <div className="inline-flex items-center px-4 py-2 text-sm font-medium border-l-2 border-[var(--accent-primary)] bg-[var(--card-bg)] text-[var(--text-primary)] transition-all duration-200 hover:bg-[var(--hover-light)] rounded-md">
                              <span className="mr-2 text-[var(--accent-primary)] font-semibold">@</span>
                              {teams.find(t => t.id.toString() === selectedUserTeam.toString())?.name || ''}
                              <button
                                type="button"
                                onClick={() => {
                                  // Check if this is a temporary team
                                  const tempTeam = teams.find(t => t.id.toString() === selectedUserTeam.toString());
                                  if (tempTeam && tempTeam.isTemporary) {
                                    // Remove from teams array only if it's a temporary team
                                    setTeams(prev => prev.filter(t => t.id.toString() !== selectedUserTeam.toString()));
                                  }
                                  // Just clear the selected team without removing from the list
                                  setSelectedUserTeam(null);
                                }}
                                className="ml-2 p-1.5 text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--hover-dark)] rounded-full transition-colors"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowChangeTeamModal(false);
                  setSelectedUser(null);
                  setSelectedUserTeam(null);
                  setShowCreateUserTeamInput(false);
                  setNewUserTeamName('');
                  setUserTeamError('');
                }}
                className="px-4 py-2 text-[var(--text-primary)] hover:bg-[var(--hover-light)] rounded-md transition-colors"
                disabled={changingTeam}
              >
                Cancel
              </button>
              <button
                onClick={handleChangeTeam}
                disabled={changingTeam}
                className={`flex items-center px-4 py-2 rounded-md text-white transition-colors ${
                  changingTeam
                    ? 'bg-[var(--accent-primary)] opacity-50 cursor-not-allowed'
                    : 'bg-[var(--accent-primary)] hover:bg-[var(--hover-dark)]'
                }`}
              >
                {changingTeam ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    <span>Updating...</span>
                  </>
                ) : (
                  `${selectedUserTeam === 'noTeam' ? 'Remove from Team' : 'Update Team'}`
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 